import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@/i18n';
import { ApiError, api } from '@/api/client';
import type { NomineeRelation, PrivacyCentre as Centre } from '@/api/types';
import { Alert, Button, Card, Chip, Field, Input, Select, Skeleton, TextArea, useToast } from '@/components/ui';
import { formatDate } from '@/lib/format';
import { GRIEVANCE_OFFICER } from '@/content/company';

const RELATIONS: NomineeRelation[] = ['spouse', 'parent', 'sibling', 'grandparent', 'relative', 'friend', 'other'];
const ASK_KINDS = ['correction', 'withdraw_consent', 'nominee_request', 'other'] as const;

/**
 * Privacy & consent centre (DPDP Act): what you agreed to and when, your nominee, and your data
 * requests — with a way to ask the grievance officer for anything the app can't do itself.
 */
export const PrivacyCentre = () => {
  const t = useT();
  const toast = useToast();
  const [data, setData] = useState<Centre>();
  const [failed, setFailed] = useState(false);
  const [editingNominee, setEditingNominee] = useState(false);
  const [nominee, setNominee] = useState({ name: '', relation: 'spouse' as NomineeRelation, phone: '', email: '' });
  const [ask, setAsk] = useState<{ kind: (typeof ASK_KINDS)[number]; details: string }>({ kind: 'correction', details: '' });
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();

  const load = () => api.me.privacy().then(setData, () => setFailed(true));
  useEffect(() => {
    void load();
  }, []);

  const run = async (key: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(key);
    setError(undefined);
    try {
      await fn();
      await load();
      toast(done);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      return false;
    } finally {
      setBusy(undefined);
    }
  };

  const saveNominee = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await run(
      'nominee',
      () =>
        api.me.setNominee({
          name: nominee.name.trim(),
          relation: nominee.relation,
          phone: nominee.phone.trim() || undefined,
          email: nominee.email.trim() || undefined,
        }),
      t('privacy.nominee.saved'),
    );
    if (ok) setEditingNominee(false);
  };

  const sendAsk = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await run('ask', () => api.me.ask({ kind: ask.kind, details: ask.details.trim() }), t('privacy.ask.sent'));
    if (ok) setAsk({ ...ask, details: '' });
  };

  const editNominee = () => {
    const n = data?.nominee;
    setNominee({ name: n?.name ?? '', relation: n?.relation ?? 'spouse', phone: n?.phone ?? '', email: n?.email ?? '' });
    setEditingNominee(true);
  };

  return (
    <Card id="privacy" className="mb-4 scroll-mt-24 space-y-6">
      <div>
        <h2 className="text-h3 text-ink">{t('privacy.title')}</h2>
        <p className="mt-1 text-body text-ink-soft">{t('privacy.body')}</p>
      </div>
      {error && <Alert tone="warn">{error}</Alert>}
      {failed ? (
        <Alert tone="warn">{t('common.error')}</Alert>
      ) : !data ? (
        <Skeleton lines={4} />
      ) : (
        <>
          <section aria-labelledby="consent-history">
            <h3 id="consent-history" className="text-label text-ink">{t('privacy.history')}</h3>
            <ol className="mt-2 grid gap-1.5 border-l-2 border-line pl-4">
              {data.consentHistory.map((e, i) => (
                <li key={`${e.at}-${i}`} className="relative text-caption">
                  <span aria-hidden="true" className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-sage-500" />
                  <span className="text-ink">{t(`privacy.event.${e.kind}.${e.action}`, { version: e.version ?? '' })}</span>
                  <span className="text-ink-muted"> · {formatDate(e.at)}</span>
                </li>
              ))}
            </ol>
            <p className="mt-3 text-caption text-ink-muted">
              {t('privacy.withdraw')}{' '}
              <a href="#your-data" className="font-bold text-lagoon-700 hover:underline">
                {t('settings.data.title')}
              </a>
            </p>
          </section>

          <section aria-labelledby="nominee" className="rounded-lg bg-paper p-4">
            <h3 id="nominee" className="text-label text-ink">{t('privacy.nominee.title')}</h3>
            <p className="mt-1 text-caption text-ink-soft">{t('privacy.nominee.body')}</p>
            {!editingNominee ? (
              data.nominee ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-body">
                    <span className="font-bold">{data.nominee.name}</span>
                    <span className="text-ink-muted"> · {t(`privacy.relation.${data.nominee.relation}`)} · {data.nominee.phone ?? data.nominee.email}</span>
                  </p>
                  <span className="flex gap-2">
                    <Button variant="secondary" onClick={editNominee}>
                      {t('privacy.nominee.change')}
                    </Button>
                    <Button variant="tertiary" loading={busy === 'removeNominee'} onClick={() => void run('removeNominee', () => api.me.removeNominee(), t('privacy.nominee.removed'))}>
                      {t('privacy.nominee.remove')}
                    </Button>
                  </span>
                </div>
              ) : (
                <Button variant="secondary" className="mt-3" onClick={editNominee}>
                  {t('privacy.nominee.add')}
                </Button>
              )
            ) : (
              <form className="mt-3 grid gap-3" onSubmit={(e) => void saveNominee(e)} noValidate>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field id="nominee-name" label={t('privacy.nominee.name')}>
                    <Input value={nominee.name} maxLength={80} onChange={(e) => setNominee({ ...nominee, name: e.target.value })} />
                  </Field>
                  <Field id="nominee-relation" label={t('privacy.nominee.relation')}>
                    <Select value={nominee.relation} onChange={(e) => setNominee({ ...nominee, relation: e.target.value as NomineeRelation })}>
                      {RELATIONS.map((r) => (
                        <option key={r} value={r}>
                          {t(`privacy.relation.${r}`)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field id="nominee-phone" label={t('privacy.nominee.phone')} hint={t('privacy.nominee.phoneHint')}>
                    <Input type="tel" inputMode="tel" value={nominee.phone} onChange={(e) => setNominee({ ...nominee, phone: e.target.value })} />
                  </Field>
                  <Field id="nominee-email" label={t('privacy.nominee.email')}>
                    <Input type="email" value={nominee.email} onChange={(e) => setNominee({ ...nominee, email: e.target.value })} />
                  </Field>
                </div>
                <p className="text-caption text-ink-muted">{t('privacy.nominee.tell')}</p>
                <div className="flex gap-2">
                  <Button type="submit" loading={busy === 'nominee'}>
                    {t('common.save')}
                  </Button>
                  <Button type="button" variant="tertiary" onClick={() => setEditingNominee(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            )}
          </section>

          <section aria-labelledby="requests">
            <h3 id="requests" className="text-label text-ink">{t('privacy.requests')}</h3>
            {data.requests.length === 0 ? (
              <p className="mt-1 text-caption text-ink-muted">{t('privacy.requests.none')}</p>
            ) : (
              <ul className="mt-2 divide-y divide-line text-caption">
                {data.requests.map((r) => (
                  <li key={r.requestId} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                    <span className="text-ink">{t(`privacy.kind.${r.kind}`)}</span>
                    <span className="text-ink-muted">
                      {formatDate(r.createdAt)} · {t(`privacy.status.${r.status}`)}
                      {r.resolutionNote ? ` · ${r.resolutionNote}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <form aria-labelledby="ask" className="grid gap-3 border-t border-line pt-5" onSubmit={(e) => void sendAsk(e)} noValidate>
            <h3 id="ask" className="text-label text-ink">{t('privacy.ask.title')}</h3>
            <div role="radiogroup" aria-label={t('privacy.ask.title')} className="flex flex-wrap gap-2">
              {ASK_KINDS.map((k) => (
                <Chip key={k} role="radio" selected={ask.kind === k} onToggle={() => setAsk({ ...ask, kind: k })}>
                  {t(`privacy.kind.${k}`)}
                </Chip>
              ))}
            </div>
            <Field id="ask-details" label={t('privacy.ask.details')} hint={t('privacy.ask.hint', { days: GRIEVANCE_OFFICER.resolveDays })}>
              <TextArea value={ask.details} maxLength={1000} rows={3} onChange={(e) => setAsk({ ...ask, details: e.target.value })} />
            </Field>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" variant="secondary" loading={busy === 'ask'} disabled={ask.details.trim().length < 10}>
                {t('privacy.ask.send')}
              </Button>
              <Link to="/trust" className="text-caption font-bold text-lagoon-700 hover:underline">
                {t('footer.trust')} →
              </Link>
            </div>
          </form>
        </>
      )}
    </Card>
  );
};
